





























import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  PropType,
} from '@vue/composition-api';
import { Button } from '@hb/coffee';
import FlattenContract from '@/widgets/contract-view/flatten-contract/index.vue';
import type { ContractListResponse, ContractListItem } from '@/widgets/contract-view/type';
import { creditMallApply } from '@/api/rights/shop-mall';
import { VerifySceneEnum, OpenVerifyPageMode } from '@/shared/verify-credit-controller/types';
import jumpToVerifyCredit from '@/shared/verify-credit-controller';
import { NewVersionEnum } from '@/enums/newVersionRoute';
import { jumpToNewVersion } from '@/shared/utils/jumpTo';
import { RouterV2Enum } from '@/router/router-names';
import { ubtSendError } from '@/shared/utils/ubt';

export default defineComponent({
  name: 'FqContract',
  components: {
    FlattenContract,
    Button,
  },
  props: {
    /** 分期商城渠道协议列表 */
    fqChannelList: {
      type: Array as PropType<ContractListResponse[]>,
      required: true,
    },
    /** 投放来源 */
    from: {
      type: String,
      required: true,
    },
  },
  setup(props, context) {
    const router = context.root.$router;

    /** 倒计时秒数 */
    const countdown = ref(5);
    /** 倒计时定时器 */
    let countdownTimer: number | null = null;

    /** 授信单号 */
    const currentCreditTaskId = ref('');

    /** 分期商城产品ID列表 */
    const fqProductIds = computed(
      () => props.fqChannelList.map((item) => item.productId),
    );

    /**
     * 将所有渠道的协议扁平化为单一列表
     * 为每个协议名称添加渠道前缀，便于区分
     */
    const flattenedContractList = computed<ContractListItem[]>(() => {
      const result: ContractListItem[] = [];
      props.fqChannelList.forEach((channel) => {
        channel.contractList.forEach((contract) => {
          result.push({
            name: `【${channel.productName}】${contract.name}`,
            url: contract.url,
            type: contract.type,
          });
        });
      });
      return result;
    });

    /** 处理协议切换 - 内部处理，不向外抛出 */
    const handleChangeContract = (index: number) => {
      // 协议切换事件处理
      // 可以在这里添加埋点等逻辑
      const currentContract = flattenedContractList.value[index];
      if (currentContract) {
        // 埋点或其他业务逻辑
      }
    };

    /** 按钮是否禁用 */
    const isButtonDisabled = computed(() => countdown.value > 0);

    /** 按钮文案 */
    const buttonText = computed(() => {
      if (countdown.value > 0) {
        return `已阅读并同意协议（${countdown.value}s）`;
      }
      return '已阅读并同意协议';
    });

    /** 跳转参数 */
    const jumpQuery = computed<Dictionary<string | boolean | undefined>>(() => ({
      from: props.from,
    }));

    /**
     * 启动倒计时
     */
    const startCountdown = () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
      countdown.value = 5;
      countdownTimer = window.setInterval(() => {
        countdown.value -= 1;
        if (countdown.value <= 0) {
          if (countdownTimer) {
            clearInterval(countdownTimer);
            countdownTimer = null;
          }
        }
      }, 1000);
    };

    /**
     * 跳转到等待页
     */
    const jumpToWaitingPage = () => {
      router.replace({
        path: RouterV2Enum.unitWaitingPage,
        query: {
          creditTaskId: currentCreditTaskId.value,
          ...jumpQuery.value,
        },
      });
    };

    /**
     * 跳转到失败页
     */
    const jumpToFailedPage = (errorMessage: string) => {
      jumpToNewVersion(NewVersionEnum.HALF_PROCESS_FAILED, true);
      ubtSendError(errorMessage);
    };

    /**
     * 跳转到资料补充页
     */
    const jumpToVerifyCreditPage = () => {
      jumpToVerifyCredit(VerifySceneEnum.PRE_CREDIT_FQ_SHOPPING, {
        openMode: OpenVerifyPageMode.POPUP_SHOW,
        successCb: async () => {
          // eslint-disable-next-line no-use-before-define
          executeApplyLogic();
        },
      });
    };

    /**
     * 执行申请逻辑的核心函数
     */
    const executeApplyLogic = async () => {
      try {
        /** 未申请过授信，则申请授信 */
        if (!currentCreditTaskId.value) {
          const result = await creditMallApply({
            from: props.from,
            productIds: fqProductIds.value,
          });
          if (result?.creditTaskId) {
            currentCreditTaskId.value = result.creditTaskId;
          }
        }
        // 如果申请授信成功，跳转到等待页
        if (currentCreditTaskId.value) {
          jumpToWaitingPage();
        } else {
          // 授信成功但未返回 creditTaskId，跳转到失败页
          jumpToFailedPage('creditMallApply success but no creditTaskId');
        }
      } catch (error) {
        // 如果申请授信失败并且错误码为300002，需要补充资料
        if ((error as Dictionary).code === 300002) {
          // 弹出资料补充页
          jumpToVerifyCreditPage();
        } else {
          // 其他错误，跳转到失败页
          jumpToFailedPage('creditMallApply error');
        }
      }
    };

    /**
     * 处理申请按钮点击
     */
    const handleApply = () => {
      executeApplyLogic();
    };

    onMounted(() => {
      startCountdown();
    });

    onBeforeUnmount(() => {
      // 清理定时器
      if (countdownTimer) {
        clearInterval(countdownTimer);
        countdownTimer = null;
      }
    });

    return {
      flattenedContractList,
      isButtonDisabled,
      buttonText,
      handleChangeContract,
      handleApply,
    };
  },
});
